import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FileText, Image as ImageIcon, ExternalLink, Download, Search, Upload, Loader2, Pencil, Trash2, MessageSquare, Tag, DollarSign, ClipboardList, BarChart3, Calculator, Sparkles, Files, Building2 } from 'lucide-react';
import PresentationsButton from './PresentationsButton';
import { useChecklistCreator } from '../contexts/ChecklistContext';
import FinancialSummaryTab from './financial/FinancialSummaryTab';
import BudgetBuilderTab from './financial/BudgetBuilderTab';
import BudgetAnalyzerTab from './financial/BudgetAnalyzerTab';
import CapExTab from './financial/CapExTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'cap-ex':      '#003DA5',
  'op-ex':       '#1F4FBE',
  'ff-e':        '#8B5CF6',
  construction:  '#FF6F59',
  consulting:    '#14B8A6',
  'pre-opening': '#EAB308',
  forecast:      '#22C55E',
  general:       '#7A8C8C',
};

const CATEGORY_LABELS = {
  'cap-ex':      'CapEx',
  'op-ex':       'OpEx',
  'ff-e':        'FF&E',
  construction:  'Construction',
  consulting:    'Consulting',
  'pre-opening': 'Pre-Opening',
  forecast:      'Forecast',
  general:       'General',
};

function fileExt(name) { return (name?.split('.').pop() || '').toLowerCase(); }
function isImage(ext) { return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext); }
function isPdf(ext) { return ext === 'pdf'; }
function isSpreadsheet(ext) { return ['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext); }
function formatAmount(amt) {
  if (amt == null || Number.isNaN(amt)) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(amt);
  } catch { return `$${amt}`; }
}

export default function PropertyBudgetsDialog({ open, onClose }) {
  const [budgets, setBudgets] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('summary');
  const fileRef = useRef(null);
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  // Singleton checklist creator (lives at App level — see
  // contexts/ChecklistContext.jsx for the rationale on why this is NOT
  // mounted inline inside this Dialog tree).
  const checklist = useChecklistCreator();

  const loadBudgets = () => {
    axios.get(`${API}/property-budgets`).then(({ data }) => setBudgets(data)).catch(() => {});
  };

  useEffect(() => {
    if (!open) return;
    loadBudgets();
  }, [open]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!adminToken) { alert('You must be signed in as admin to upload budget documents.'); return; }
    // iter-122 — Multi-file budget upload. For a single file we keep
    // the original Title + Category + Amount prompts. For a batch we
    // ask Category ONCE, use each file's filename as the Title, and
    // skip Amount (user can edit inline afterwards).
    let batchCategory = null;
    let batchTitle = null;
    let batchAmount = null;
    if (files.length === 1) {
      const f = files[0];
      const defaultTitle = f.name.replace(/\.[^.]+$/, '');
      batchTitle = window.prompt('Budget title:', defaultTitle);
      if (!batchTitle) { if (fileRef.current) fileRef.current.value = ''; return; }
      const categoryKeys = Object.keys(CATEGORY_LABELS);
      batchCategory = window.prompt(`Category (${categoryKeys.join(', ')}):`, 'cap-ex') || 'general';
      const amountRaw = window.prompt('Headline amount in USD (optional, leave blank to skip):', '');
      batchAmount = amountRaw && !Number.isNaN(parseFloat(amountRaw)) ? parseFloat(amountRaw) : null;
    } else {
      const categoryKeys = Object.keys(CATEGORY_LABELS);
      batchCategory = window.prompt(
        `Uploading ${files.length} files. Category for all (${categoryKeys.join(', ')}):`,
        'cap-ex',
      );
      if (!batchCategory) { if (fileRef.current) fileRef.current.value = ''; return; }
    }
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('keep_raw', 'true');
        // eslint-disable-next-line no-await-in-loop
        const { data: upload } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        const extName = (file.name.split('.').pop() || '').toLowerCase();
        const titleForFile = batchTitle || file.name.replace(/\.[^.]+$/, '');
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/admin/property-budgets`, {
          title: titleForFile,
          category: batchCategory,
          amount: batchAmount,
          file_url: `${BACKEND}${upload.url}`,
          file_name: file.name,
          file_type: extName,
          order: budgets.length + i,
        }, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true });
        ok += 1;
      } catch (err) {
        console.error('Budget upload failed for', file.name, err);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    if (ok < files.length) {
      alert(`Uploaded ${ok}/${files.length} files. Some failed — check the console.`);
    }
    loadBudgets();
  };

  const adminAuth = adminToken
    ? { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true }
    : null;

  const patchBudget = async (id, patch) => {
    if (!adminAuth) return;
    try {
      await axios.put(`${API}/admin/property-budgets/${id}`, patch, adminAuth);
      loadBudgets();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Update failed.');
    }
  };

  const handleRename = (b, e) => {
    e.stopPropagation();
    const title = window.prompt('New title:', b.title);
    if (!title || title === b.title) return;
    patchBudget(b.id, { title });
  };

  const handleDescription = (b, e) => {
    e.stopPropagation();
    const description = window.prompt('Description / notes:', b.description || '');
    if (description === null) return;
    patchBudget(b.id, { description });
  };

  const handleChangeCategory = (b, e) => {
    e.stopPropagation();
    const keys = Object.keys(CATEGORY_LABELS);
    const next = window.prompt(
      `Category (${keys.join(', ')}):`,
      b.category || 'general'
    );
    if (!next || next === b.category) return;
    patchBudget(b.id, { category: next });
  };

  const handleChangeAmount = (b, e) => {
    e.stopPropagation();
    const current = b.amount != null ? String(b.amount) : '';
    const raw = window.prompt('Headline amount in USD (blank to clear):', current);
    if (raw === null) return;
    const amount = raw.trim() === '' ? null : parseFloat(raw);
    if (raw.trim() !== '' && Number.isNaN(amount)) { alert('Not a valid number.'); return; }
    patchBudget(b.id, { amount });
  };

  const handleDelete = async (b, e) => {
    e.stopPropagation();
    if (!adminAuth) return;
    if (!window.confirm(`Delete "${b.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/property-budgets/${b.id}`, adminAuth);
      loadBudgets();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed.');
    }
  };

  const categories = useMemo(() => {
    const set = new Set(budgets.map((b) => b.category || 'general'));
    return ['all', ...Array.from(set)];
  }, [budgets]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return budgets.filter((b) => {
      if (filter !== 'all' && (b.category || 'general') !== filter) return false;
      if (q && !`${b.title} ${b.description || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [budgets, filter, query]);

  const totalAmount = useMemo(
    () => budgets.reduce((sum, b) => sum + (typeof b.amount === 'number' ? b.amount : 0), 0),
    [budgets]
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent data-testid="property-budgets-dialog" className="w-[95vw] max-w-4xl max-h-[92vh] overflow-y-auto p-0" style={{ backgroundColor: '#F4F1EB' }}>
          <div className="p-5 sm:p-6">
            <DialogHeader className="mb-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <DialogTitle className="text-2xl sm:text-3xl flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
                    <DollarSign className="w-6 h-6" style={{ color: '#003DA5' }} /> Financial Center
                    <PresentationsButton deptId="property-budgets" deptLabel="Property Budgets" />
                  </DialogTitle>
                  <p className="text-xs mt-1" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
                    Live budget builder · category roll-ups · AI ROI analysis · CapEx/OpEx/FF&amp;E document library.
                    {totalAmount > 0 && (
                      <span className="ml-2 font-bold" style={{ color: '#003DA5' }}>
                        Tracked: {formatAmount(totalAmount)}
                      </span>
                    )}
                  </p>
                </div>
                {adminToken && (
                  <>
                    <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.xlsx,.xls,.csv,.ods,.numbers,.doc,.docx" className="hidden" onChange={handleUpload} />
                    <button
                      data-testid="budgets-checklist-btn"
                      onClick={() => checklist.open({ workspaceLabel: 'Property Budgets', adminToken })}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white transition-all hover:-translate-y-0.5 flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #1F4FBE 0%, #003DA5 100%)', fontFamily: 'Manrope' }}
                      title="Build a Hyatt-branded budget checklist"
                    >
                      <ClipboardList className="w-3.5 h-3.5" /> Checklist
                    </button>
                    <button
                      data-testid="budget-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 flex-shrink-0"
                      style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
                    >
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? 'Uploading…' : 'Upload Budget'}
                    </button>
                  </>
                )}
              </div>
            </DialogHeader>

            {/* Tab strip — switches between Financial Center surfaces. CapEx
                lives between Builder and AI Analysis because the GM authors
                CapEx docs more often than uploads them. */}
            <div data-testid="financial-tabstrip" className="inline-flex items-center gap-1 p-1 rounded-full mb-4" style={{ backgroundColor: '#F0F4FA' }}>
              {[
                { id: 'summary',   label: 'Summary',  icon: BarChart3,  testid: 'fc-tab-summary' },
                { id: 'builder',   label: 'Builder',  icon: Calculator, testid: 'fc-tab-builder' },
                { id: 'capex',     label: 'CapEx',    icon: Building2,  testid: 'fc-tab-capex' },
                { id: 'analyzer',  label: 'AI Analysis', icon: Sparkles, testid: 'fc-tab-analyzer' },
                { id: 'documents', label: 'Documents', icon: Files,     testid: 'fc-tab-documents' },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={t.testid}
                  onClick={() => setTab(t.id)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold transition-all"
                  style={{
                    backgroundColor: tab === t.id ? '#003DA5' : 'transparent',
                    color: tab === t.id ? 'white' : '#4A5D5D',
                    fontFamily: 'Manrope',
                  }}
                >
                  <t.icon className="w-3 h-3" /> {t.label}
                </button>
              ))}
            </div>

            {tab === 'summary' && <FinancialSummaryTab adminToken={adminToken} />}
            {tab === 'builder' && <BudgetBuilderTab adminToken={adminToken} />}
            {tab === 'capex' && <CapExTab adminToken={adminToken} budgets={budgets} onReload={loadBudgets} />}
            {tab === 'analyzer' && <BudgetAnalyzerTab adminToken={adminToken} />}

            {tab === 'documents' && (
              <>
            {/* Toolbar */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <div className="relative flex-1 min-w-[160px]">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#7A8C8C' }} />
                <input
                  data-testid="budgets-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search budgets…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ borderColor: '#A3C4EE', fontFamily: 'Manrope', backgroundColor: 'white' }}
                />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilter(c)}
                    data-testid={`budgets-filter-${c}`}
                    className="px-3 py-1.5 rounded-full text-[10px] font-bold transition-all"
                    style={{
                      backgroundColor: filter === c ? (CATEGORY_COLORS[c] || '#1A2E2E') : '#EAE5D9',
                      color: filter === c ? 'white' : '#4A5D5D',
                      fontFamily: 'Manrope',
                    }}
                  >
                    {c === 'all' ? `All (${budgets.length})` : `${CATEGORY_LABELS[c] || c} (${budgets.filter((b) => (b.category || 'general') === c).length})`}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-12" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">
                  {budgets.length === 0
                    ? (adminToken
                        ? 'No budget documents have been uploaded yet. Click "Upload Budget" above to add the first one.'
                        : 'No budget documents have been uploaded yet. Please check back soon.')
                    : 'No budgets match your search or filter.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {filtered.map((b) => {
                  const ext = fileExt(b.file_name || b.file_url);
                  const color = CATEGORY_COLORS[b.category] || '#7A8C8C';
                  const openBudget = () => (isImage(ext) ? setPreview(b) : window.open(b.file_url, '_blank'));
                  return (
                    <div
                      key={b.id}
                      data-testid={`budget-card-${b.id}`}
                      className="group relative text-left rounded-2xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl cursor-pointer"
                      style={{ backgroundColor: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}
                      onClick={openBudget}
                    >
                      <div className="aspect-[4/3] relative overflow-hidden" style={{ backgroundColor: '#EAE5D9' }}>
                        {isImage(ext) ? (
                          <img src={b.file_url} alt={b.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                            {isPdf(ext) ? (
                              <FileText className="w-10 h-10" style={{ color }} />
                            ) : isSpreadsheet(ext) ? (
                              <DollarSign className="w-10 h-10" style={{ color }} />
                            ) : (
                              <ImageIcon className="w-10 h-10" style={{ color }} />
                            )}
                            <span className="text-[10px] font-bold uppercase" style={{ color, fontFamily: 'Manrope' }}>.{ext}</span>
                          </div>
                        )}
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: color, fontFamily: 'Manrope' }}>
                          {CATEGORY_LABELS[b.category] || b.category || 'General'}
                        </span>
                        {b.amount != null && (
                          <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-md" style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}>
                            {formatAmount(b.amount)}
                          </span>
                        )}
                        {adminToken && (
                          <div
                            className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              title="Rename"
                              onClick={(e) => handleRename(b, e)}
                              data-testid={`budget-rename-${b.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: 'white', color: '#003DA5' }}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Edit description"
                              onClick={(e) => handleDescription(b, e)}
                              data-testid={`budget-desc-${b.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: 'white', color: '#003DA5' }}
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Change category"
                              onClick={(e) => handleChangeCategory(b, e)}
                              data-testid={`budget-category-${b.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: 'white', color: '#003DA5' }}
                            >
                              <Tag className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Edit amount"
                              onClick={(e) => handleChangeAmount(b, e)}
                              data-testid={`budget-amount-${b.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: 'white', color: '#003DA5' }}
                            >
                              <DollarSign className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              onClick={(e) => handleDelete(b, e)}
                              data-testid={`budget-delete-${b.id}`}
                              className="w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110"
                              style={{ backgroundColor: '#EF4444', color: 'white' }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-bold truncate" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>{b.title}</p>
                        {b.description && <p className="text-[10px] mt-0.5 line-clamp-2" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{b.description}</p>}
                        <div className="flex items-center gap-2 mt-2 text-[10px]" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                          <span className="flex items-center gap-1 font-bold">{isImage(ext) ? 'View' : 'Open'} <ExternalLink className="w-3 h-3" /></span>
                          <span>·</span>
                          <a href={b.file_url} download onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 font-bold">
                            Download <Download className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {preview && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/90 p-4"
          style={{ zIndex: 10003 }}
          onClick={() => setPreview(null)}
          data-testid="budget-preview"
        >
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center" onClick={() => setPreview(null)}>✕</button>
          <img src={preview.file_url} alt={preview.title} className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs font-bold text-white" style={{ backgroundColor: 'rgba(0,0,0,0.6)', fontFamily: 'Manrope' }}>
            {preview.title}
          </div>
        </div>
      )}

      {/* ChecklistCreator is mounted globally (App.js via
          ChecklistCreatorProvider). Inline mounting here was racing
          Radix Dialog's outside-click handler; see
          contexts/ChecklistContext.jsx for the full reasoning. */}
    </>
  );
}
